// queryBuilder.js
// منطق خالص (بدون React) برای ساخت کوئری SELECT از روی shaxesList و edgeList
// موجود در Redux شما.
//
// ✅ اصلاح مهم نسبت به نسخهٔ قبلی:
// الیاس هر ستون در سراسر برنامهٔ شما (SelectedItems، ریدیوسر CHANGE_COLUMN_ALIAS
// و CHANGE_SELECTED_FIELD) زیر کلید `selectedColumns[].alias` نگه‌داری می‌شود؛
// نسخهٔ قبلی این فایل اشتباهاً از `col.labelAlias` می‌خواند (که اصلاً روی
// selectedColumns ست نمی‌شود) و در نتیجه الیاسی که کاربر تایپ می‌کرد هیچ‌وقت
// در کوئری تولیدی ظاهر نمی‌شد. همین‌جا هم اصلاح شد.

export function sqlIdent(name) {
  const cleaned = String(name ?? '').replace(/[[\]]/g, '').trim();
  return `[${cleaned || 'field'}]`;
}

export function hasSelectedColumns(shaxesList) {
  return (shaxesList || []).some((s) => (s.selectedColumns || []).length > 0);
}

export function formatValue(v) {
  const trimmed = String(v ?? '').trim();
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed; // عددی
  return `'${trimmed.replace(/'/g, "''")}'`;
}

// sourceField / targetField در edge شما به‌صورت `name*^tableName` یا فقط `name` ذخیره شده‌اند
export function rawFieldName(field) {
  if (!field) return '';
  return String(field).split('*^')[0];
}

// برای هر نمونهٔ جدول روی بوم یک alias یکتا و معتبر برای SQL می‌سازد.
export function buildAliasMap(shaxesList) {
  const used = new Set();
  const map = {};
  (shaxesList || []).forEach(s => {
    const tid = s.uniqueId?.toString();
    const existingAlias =
      s.tableAlias ||
      s.selectedColumns?.find(c => c.tableAlias)?.tableAlias ||
      s.columns?.find(c => c.tableAlias)?.tableAlias;
    let base = String(existingAlias || s.name || s.tableName || `t${tid}`)
      .replace(/[^\w\u0600-\u06FF]+/g, '_')
      .replace(/^_+|_+$/g, '') || 't';
    let alias = base, n = 1;
    while (used.has(alias)) alias = `${base}_${n++}`;
    used.add(alias);
    map[tid] = alias;
  });
  return map;
}

const JOIN_KEYWORDS = { INNER: 'INNER JOIN', LEFT: 'LEFT JOIN', RIGHT: 'RIGHT JOIN', FULL: 'FULL JOIN' };

// یک منبع واحد برای ظاهر و توضیح هر نوع JOIN — هم برای متن کامل JOIN در کوئری
// استفاده می‌شود، هم در CustomEdge برای رنگ/خط‌چین/آیکن روی خط ارتباط.
// icon: کلید نمادین آیکنی که روی خط رابطه نمایش داده می‌شود:
//   'right'  => فقط سمت راست کامل نمایش داده می‌شود (RIGHT JOIN)
//   'left'   => فقط سمت چپ کامل نمایش داده می‌شود (LEFT JOIN)
//   'both'   => هر دو طرف کامل نمایش داده می‌شوند (FULL JOIN)
//   'none'   => هیچ‌کدام؛ فقط اشتراک دو طرف (INNER JOIN)
export const JOIN_VISUALS = {
  INNER: { color: '#4FD1C5', dash: undefined, icon: 'none', label: 'INNER JOIN', description: 'فقط رکوردهای مشترک بین دو جدول' },
  LEFT: { color: '#E7A33E', dash: '7,4', icon: 'left', label: 'LEFT JOIN', description: 'همهٔ رکوردهای سمت چپ + مشترک‌های سمت راست' },
  RIGHT: { color: '#E2725B', dash: '2,4', icon: 'right', label: 'RIGHT JOIN', description: 'همهٔ رکوردهای سمت راست + مشترک‌های سمت چپ' },
  FULL: { color: '#B18CD9', dash: '8,3,2,3', icon: 'both', label: 'FULL JOIN', description: 'همهٔ رکوردهای هر دو جدول' },
};

/**
 * BFS از جدول پایه روی edgeList. ارتباط‌های تکراری/اضافه بین دو جدولی که هر دو
 * از قبل شامل شده‌اند به‌جای دورریخته‌شدن، به‌عنوان شرط AND اضافه به همان JOIN می‌چسبند.
 */
export function buildJoinPlan(baseId, edgeList) {
  const includedOrder = [baseId];
  const included = new Set(includedOrder);
  const joinStepByTable = {};
  const joinSteps = [];
  const usedEdgeIds = new Set();
  let pool = (edgeList || []).slice();
  let progress = true;

  while (progress) {
    progress = false;
    for (let i = 0; i < pool.length; i++) {
      const e = pool[i];
      const fromIn = included.has(e.source), toIn = included.has(e.target);
      if (fromIn && !toIn) {
        const step = {
          joinType: e.data?.joinType || 'INNER',
          newTable: e.target,
          conditions: [{ leftTable: e.source, leftField: rawFieldName(e.sourceField), rightField: rawFieldName(e.targetField) }]
        };
        joinSteps.push(step); joinStepByTable[e.target] = step;
        included.add(e.target); includedOrder.push(e.target);
        usedEdgeIds.add(e.id); pool.splice(i, 1); i--; progress = true;
      } else if (toIn && !fromIn) {
        const step = {
          joinType: e.data?.joinType || 'INNER',
          newTable: e.source,
          conditions: [{ leftTable: e.target, leftField: rawFieldName(e.targetField), rightField: rawFieldName(e.sourceField) }]
        };
        joinSteps.push(step); joinStepByTable[e.source] = step;
        included.add(e.source); includedOrder.push(e.source);
        usedEdgeIds.add(e.id); pool.splice(i, 1); i--; progress = true;
      }
    }
  }

  // ارتباط‌های اضافه/تکراری بین دو جدولی که هر دو از قبل داخل included هستند
  (edgeList || []).forEach(e => {
    if (usedEdgeIds.has(e.id)) return;
    if (e.source === e.target) return;
    const fromIn = included.has(e.source), toIn = included.has(e.target);
    if (!(fromIn && toIn)) return;

    let ownerTable, ownerField, refTable, refField;
    if (e.source === baseId) {
      ownerTable = e.target; ownerField = rawFieldName(e.targetField);
      refTable = e.source; refField = rawFieldName(e.sourceField);
    } else if (e.target === baseId) {
      ownerTable = e.source; ownerField = rawFieldName(e.sourceField);
      refTable = e.target; refField = rawFieldName(e.targetField);
    } else {
      const idxA = includedOrder.indexOf(e.source), idxB = includedOrder.indexOf(e.target);
      if (idxA > idxB) { ownerTable = e.source; ownerField = rawFieldName(e.sourceField); refTable = e.target; refField = rawFieldName(e.targetField); }
      else { ownerTable = e.target; ownerField = rawFieldName(e.targetField); refTable = e.source; refField = rawFieldName(e.sourceField); }
    }
    const step = joinStepByTable[ownerTable];
    if (step) step.conditions.push({ leftTable: refTable, leftField: refField, rightField: ownerField });
  });

  return { included, joinSteps };
}

/**
 * @param {Object} params
 * @param {Array} params.shaxesList  آرایهٔ جدول‌های روی بوم (از Redux)
 * @param {Array} params.edgeList    آرایهٔ ارتباط‌ها (از Redux)
 * @param {String} params.baseId     uniqueId جدول پایه (به‌صورت رشته)
 * @param {Array} params.whereRows   [{fieldKey:'uniqueId::fieldName', operator, value, connector}]
 * @param {Array} params.orderRows   [{fieldKey:'uniqueId::fieldName', dir:'ASC'|'DESC'}]
 * @param {Array} params.groupByRows [{fieldKey:'uniqueId::fieldName'}]
 * @param {Object} params.aggregates نگاشت 'uniqueId::fieldName' -> 'NONE'|'COUNT'|'SUM'|'AVG'|'MIN'|'MAX'
 */
export function buildSelectQuery({ shaxesList = [], edgeList = [], baseId, whereRows = [], orderRows = [], groupByRows = [], aggregates = {}, customColumns = [],columnOrder = [],distinct = false, top = { type: 'NONE', value: null } }) {
  if (!baseId || shaxesList.length === 0) {
    return '-- ابتدا حداقل یک جدول به بوم اضافه کنید و جدول پایه را انتخاب کنید.';
  }
  const byId = Object.fromEntries(shaxesList.map(s => [s.uniqueId?.toString(), s]));
  const baseS = byId[baseId];
  if (!baseS) return '-- جدول پایه یافت نشد.';

  const aliasMap = buildAliasMap(shaxesList);
  const { included, joinSteps } = buildJoinPlan(baseId, edgeList);

  const selectPartsMap = {};
  const defaultOrder = [];
  const skippedTables = new Set();

  shaxesList.forEach(s => {
    const tid = s.uniqueId?.toString();
    (s.selectedColumns || []).forEach(col => {
      if (!included.has(tid)) { skippedTables.add(s.name || s.tableName); return; }
      const key = `${tid}::${col.name}`;
      const aliasLabel = (col.alias && col.alias.trim()) || col.name;
      const colRef = `${sqlIdent(aliasMap[tid])}.${sqlIdent(col.name)}`;
      const func = aggregates[key];
      const expr = (func && func !== 'NONE') ? `${func}(${colRef})` : colRef;
      selectPartsMap[key] = `${expr} AS ${sqlIdent(aliasLabel)}`;
      defaultOrder.push(key);
    });
  });

  // ترتیب نهایی: ابتدا columnOrder (ترتیبی که کاربر با درگ تعیین کرده)،
  // سپس هر ستونی که تازه انتخاب شده و هنوز در columnOrder نیست (در انتها، به ترتیب پیش‌فرض)
  const orderedKeys = [];
  const seenKeys = new Set();
  (columnOrder || []).forEach((k) => {
    if (selectPartsMap[k] && !seenKeys.has(k)) { orderedKeys.push(k); seenKeys.add(k); }
  });
  defaultOrder.forEach((k) => {
    if (!seenKeys.has(k)) { orderedKeys.push(k); seenKeys.add(k); }
  });

  const selectParts = orderedKeys.map((k) => selectPartsMap[k]);

  // ستون‌های محاسباتی/دلخواه کاربر (customColumns)
  const skippedCustomColumns = [];
  (customColumns || []).forEach((c) => {
    const aliasLabel = (c.alias || '').trim();
    const rawExpr = (c.expression || '').trim();
    if (!aliasLabel || !rawExpr) return;

    let unresolvedTable = false;
    const resolvedExpr = rawExpr.replace(/\[([^\[\]:]+)::([^\[\]]+)\]/g, (whole, tid, fieldName) => {
      if (!included.has(tid) || !aliasMap[tid]) { unresolvedTable = true; return whole; }
      return `${sqlIdent(aliasMap[tid])}.${sqlIdent(fieldName.trim())}`;
    });

    if (unresolvedTable) { skippedCustomColumns.push(aliasLabel); return; }
    selectParts.push(`(${resolvedExpr}) AS ${sqlIdent(aliasLabel)}`);
  });

  if (selectParts.length === 0) {
    if (skippedTables.size) {
      return `-- ستون‌های انتخاب‌شده فقط متعلق به جدول‌هایی است که به جدول پایه متصل نیستند: ${[...skippedTables].join('، ')}\n-- آن‌ها را با یک ارتباط به جدول پایه وصل کنید یا ستونی از جدول‌های متصل انتخاب کنید.`;
    }
    return '-- هنوز هیچ ستونی انتخاب نشده است.\n-- حداقل یک ستون از جدول‌های روی بوم انتخاب کنید.';
  }

  let sql = '';
  if (skippedTables.size) {
    sql += `-- توجه: جدول(های) ${[...skippedTables].join('، ')} به جدول پایه متصل نیستند و در کوئری لحاظ نشدند.\n`;
  }
  if (skippedCustomColumns.length) {
    sql += `-- توجه: ستون(های) محاسباتی ${skippedCustomColumns.join('، ')} به جدولی خارج از مسیر JOIN ارجاع داده‌اند و نادیده گرفته شدند.\n`;
  }

  // sql += `SELECT ${selectParts.length ? selectParts.join(',\n       ') : '*'}\nFROM ${sqlIdent(baseS.tableName || baseS.name)} AS ${sqlIdent(aliasMap[baseId])}`;
  //sql += `SELECT ${selectParts.join(',\n       ')}\nFROM ${sqlIdent(baseS.tableName || baseS.name)} AS ${sqlIdent(aliasMap[baseId])}`;

  let selectKeyword = 'SELECT';
if (distinct) selectKeyword += ' DISTINCT';

const topValue = Number(top?.value);
if (top?.type && top.type !== 'NONE' && Number.isFinite(topValue) && topValue > 0) {
  selectKeyword += ` TOP ${topValue}${top.type === 'PERCENT' ? ' PERCENT' : ''}`;
}

sql += `${selectKeyword} ${selectParts.join(',\n       ')}\nFROM ${sqlIdent(baseS.tableName || baseS.name)} AS ${sqlIdent(aliasMap[baseId])}`;

  joinSteps.forEach(step => {
    const jt = byId[step.newTable];
    if (!jt) return;
    const onClause = step.conditions.map(c =>
      `${sqlIdent(aliasMap[c.leftTable])}.${sqlIdent(c.leftField)} = ${sqlIdent(aliasMap[step.newTable])}.${sqlIdent(c.rightField)}`
    ).join(' AND ');
    const kw = JOIN_KEYWORDS[step.joinType] || 'INNER JOIN';
    sql += `\n${kw} ${sqlIdent(jt.tableName || jt.name)} AS ${sqlIdent(aliasMap[step.newTable])} ON ${onClause}`;
  });

  if (whereRows.length) {
    const clause = whereRows.map((r, i) => {
      const [tid, fieldName] = String(r.fieldKey).split('::');
      const colRef = `${sqlIdent(aliasMap[tid])}.${sqlIdent(fieldName)}`;
      let cond;
      if (r.operator === 'IS NULL' || r.operator === 'IS NOT NULL') {
        cond = `${colRef} ${r.operator}`;
      } else if (r.operator === 'IN') {
        const vals = (r.value || '').split(',').map(v => formatValue(v.trim())).filter(Boolean);
        cond = `${colRef} IN (${vals.join(', ')})`;
      } else {
        cond = `${colRef} ${r.operator} ${formatValue(r.value || '')}`;
      }
      return (i > 0 ? `${r.connector} ` : '') + cond;
    }).join('\n  ');
    sql += `\nWHERE ${clause}`;
  }

  if (groupByRows.length) {
    const clause = groupByRows.map(r => {
      const [tid, fieldName] = String(r.fieldKey).split('::');
      return `${sqlIdent(aliasMap[tid])}.${sqlIdent(fieldName)}`;
    }).join(', ');
    sql += `\nGROUP BY ${clause}`;
  }

  if (orderRows.length) {
    const clause = orderRows.map(r => {
      const [tid, fieldName] = String(r.fieldKey).split('::');
      return `${sqlIdent(aliasMap[tid])}.${sqlIdent(fieldName)} ${r.dir}`;
    }).join(', ');
    sql += `\nORDER BY ${clause}`;
  }

  return sql + ';';
}

export const OPERATORS = ['=', '<>', '>', '<', '>=', '<=', 'LIKE', 'IN', 'IS NULL', 'IS NOT NULL'];
export const AGG_FUNCS = ['NONE', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX'];

// شکل پیش‌فرض تنظیمات کوئری — همان چیزی که در Redux زیر کلید queryOptions
// ذخیره و در ادیت دوباره خوانده می‌شود.
export const DEFAULT_QUERY_OPTIONS = {
  baseId: null,
  whereRows: [],
  orderRows: [],
  groupByRows: [],
  aggregates: {},
  customColumns: [],
  columnOrder: [],
  distinct: false,                     // ← جدید
  top: { type: 'NONE', value: null },  // ← جدید: type: 'NONE' | 'COUNT' | 'PERCENT'
};

/* ------------------------------------------------------------------ */
/*  اعتبارسنجی و آماده‌سازی تنظیمات کوئری                               */
/* ------------------------------------------------------------------ */

const normLabel = (s) => String(s ?? '').trim().toLowerCase();

/**
 * queryOptions ذخیره‌شده در Redux را با shaxesList فعلی هماهنگ می‌کند:
 *  - baseId کهنه (جدول حذف‌شده) را اصلاح می‌کند
 *  - ردیف‌های WHERE/GROUP/ORDER و aggregates مربوط به جدول‌های حذف‌شده را حذف می‌کند
 */
export function resolveQueryOptions(queryOptions, shaxesList) {
  const merged = { ...DEFAULT_QUERY_OPTIONS, ...(queryOptions || {}) };
  const tables = shaxesList || [];
  const ids = new Set(tables.map((s) => s.uniqueId?.toString()));

  let baseId = merged.baseId && ids.has(String(merged.baseId)) ? String(merged.baseId) : null;
  if (!baseId) {
    // ترجیحاً جدولی که ستون انتخاب‌شده دارد، وگرنه جدول اول
    const withCols = tables.find((s) => (s.selectedColumns || []).length > 0);
    baseId = (withCols || tables[0])?.uniqueId?.toString() ?? null;
  }

  const rowOk = (r) => ids.has(String(r?.fieldKey).split('::')[0]);
  const keyOk = (k) => ids.has(String(k).split('::')[0]);

  return {
    ...merged,
    baseId,
    whereRows: (merged.whereRows || []).filter(rowOk),
    orderRows: (merged.orderRows || []).filter(rowOk),
    groupByRows: (merged.groupByRows || []).filter(rowOk),
    aggregates: Object.fromEntries(Object.entries(merged.aggregates || {}).filter(([k]) => keyOk(k))),
    columnOrder: (merged.columnOrder || []).filter(keyOk),
  };
}

/**
 * نام‌های مستعار تکراری را در خروجی نهایی پیدا می‌کند
 * (ستون‌های انتخابی جدول‌های متصل به جدول پایه + ستون‌های محاسباتی).
 * خروجی: { duplicateKeys: Set<'tid::col' | 'cc::id'>, names: string[] }
 */
export function findDuplicateAliases({ shaxesList = [], edgeList = [], baseId, customColumns = [] }) {
  const included = baseId ? buildJoinPlan(baseId, edgeList).included : new Set();
  const groups = new Map();
  const add = (label, key) => {
    const n = normLabel(label);
    if (!n) return;
    if (!groups.has(n)) groups.set(n, { display: String(label).trim(), keys: [] });
    groups.get(n).keys.push(key);
  };

  (shaxesList || []).forEach((s) => {
    const tid = s.uniqueId?.toString();
    if (!included.has(tid)) return;
    const seen = new Set();
    (s.selectedColumns || []).forEach((col) => {
      const key = `${tid}::${col.name}`;
      if (seen.has(key)) return;
      seen.add(key);
      add((col.alias && col.alias.trim()) || col.name, key);
    });
  });

  (customColumns || []).forEach((c) => {
    if (!(c.alias || '').trim() || !(c.expression || '').trim()) return;
    add(c.alias, `cc::${c.id}`);
  });

  const duplicateKeys = new Set();
  const names = [];
  groups.forEach((g) => {
    if (g.keys.length > 1) {
      names.push(g.display);
      g.keys.forEach((k) => duplicateKeys.add(k));
    }
  });
  return { duplicateKeys, names };
}

/** همهٔ مراحل بالا + ساخت SQL، در یک فراخوانی */
export function prepareQuery(shaxesList, edgeList, queryOptions) {
  const options = resolveQueryOptions(queryOptions, shaxesList);
  const sql = buildSelectQuery({ shaxesList, edgeList, ...options });
  const duplicates = findDuplicateAliases({
    shaxesList, edgeList, baseId: options.baseId, customColumns: options.customColumns,
  });
  return { options, sql, duplicates };
}

/** متن خط‌های کامنت (--) در SQL تولیدی؛ برای نمایش دلیل «ساخته نشدن» */
export function sqlNoticeText(sql) {
  return String(sql || '')
    .split('\n')
    .filter((l) => l.trim().startsWith('--'))
    .map((l) => l.replace(/^\s*--\s*/, ''))
    .join(' ');
}
